import {
  CELL_BOARD_ENABLED,
  SCORECARD_ENABLED,
  COATINGS_ENABLED,
  CREWING_GUIDE_ENABLED,
  DOWNTIME_ESCALATION_ENABLED,
  JOB_STATION_ASSIGNMENT_ENABLED,
  JOB_STATIONS_ENABLED,
  REPORT_ENABLED,
  RESOURCE_TRACKING_ENABLED,
  SUPPORT_HIERARCHY_ENABLED,
  User,
  CELL_BOARD_M_CELL_COLLECTION_ID,
  CELL_BOARD_INVENTORY_CENTER_ID,
  CELL_BOARD_M_CELL_ID,
  SCHEDULING_TOOL_ENABLED,
  M_CELL_STATUS_ENABLED,
  BANNER_INFO,
  LSW_ENABLED,
  LMS_ENROLLMENT_ENABLED,
  CELL_BOARD_2_0_ENABLED,
  CELL_BOARD_SHIFT_SELECTED,
} from './types';
import { EMPTY_GUID } from '../../utils/string-utils';
import { ManufacturingLocation, MCell } from '../manufacturingLocation/types';
import { Shift } from '../resourceTracking/types';
import {
  useAllMCellsQuery,
  useInventoryCentersQuery,
  useMCellCollectionsQuery,
} from '../manufacturingLocation/queryHooks';
import { useSetUserSettingsMutation, useUserSettingsQuery } from './queryHooks';
import { UserSetting } from './types';

/**
 * Returns name from User object
 */
export const formatUserName = (user: User | undefined) => {
  if (user?.name === undefined) {
    return;
  }
  return `${user?.name}`;
};

export const shortenName = (fullName: string) => {
  let result = '';
  const nameParts = fullName.split(' ');
  result = nameParts[0][0];
  if (nameParts.length > 1) {
    result += nameParts[nameParts.length - 1][0];
  }
  return result;
};

/**
 * Hook that returns a value derived from the current user's settings. Before the settings load,
 * the selector receives an empty settings object, so it should supply its own defaults.
 * @param select - Selector over the settings. Keep it stable (defined outside the component).
 */
export const useUserSettings = <T>(select: (settings: UserSetting) => T): T => {
  const { data } = useUserSettingsQuery(select);
  return data ?? select({});
};

/**
 * Hook that returns the current selected manufacturing location
 */
export const useManufacturingLocation = () => {
  const { inventoryCenterId, mCellId, mCellCollectionId } = useUserSettings(
    selectManufacturingLocation,
  );
  const { mutate: setUserSettings } = useSetUserSettingsMutation();
  const { data: mCellCollections } = useMCellCollectionsQuery();
  const { data: inventoryCenters } = useInventoryCentersQuery();
  const { data: mCells } = useAllMCellsQuery();

  const setManufacturingLocation = (
    manufacturingLocation: ManufacturingLocation,
  ) =>
    setUserSettings({
      [CELL_BOARD_M_CELL_ID]: JSON.stringify(manufacturingLocation.mCellId),
      [CELL_BOARD_INVENTORY_CENTER_ID]: JSON.stringify(
        manufacturingLocation.inventoryCenterId,
      ),
      [CELL_BOARD_M_CELL_COLLECTION_ID]: JSON.stringify(
        manufacturingLocation.mCellCollectionId,
      ),
    });

  return {
    inventoryCenter: inventoryCenters?.find((i) => i.id === inventoryCenterId),
    mCell: mCells?.find((m) => m.id === mCellId),
    mCellCollection: mCellCollections?.find(
      (mc) => mc.id === mCellCollectionId,
    ),
    inventoryCenterId,
    mCellId,
    mCellCollectionId,
    setManufacturingLocation,
  };
};

/**
 * Parses a JSON-encoded user setting, falling back when it is absent or unparseable.
 * @param setting - The raw stored value.
 * @param fallback - Returned when there is no usable value.
 */
export const parseUserSetting = <T>(
  setting: string | undefined,
  fallback: T,
): T => {
  if (!setting) {
    return fallback;
  }
  try {
    return JSON.parse(setting) as T;
  } catch {
    return fallback;
  }
};

/**
 * Selects the Cell Board shift from user settings.
 * Falls back to 'Day' when the setting is absent, matching the default the Cell Board and Home
 * pages use for their own copy of this setting.
 */
export const selectCellBoardShift = (settings: UserSetting) =>
  parseUserSetting<Shift>(settings[CELL_BOARD_SHIFT_SELECTED], 'Day');

/**
 * Selects the MCell collection, inventory center, and MCell IDs from user settings,
 * each defaulting to `EMPTY_GUID` when absent.
 */
export const selectManufacturingLocation = (
  settings: UserSetting,
): ManufacturingLocation => ({
  mCellCollectionId: parseUserSetting(
    settings[CELL_BOARD_M_CELL_COLLECTION_ID],
    EMPTY_GUID,
  ),
  inventoryCenterId: parseUserSetting(
    settings[CELL_BOARD_INVENTORY_CENTER_ID],
    EMPTY_GUID,
  ),
  mCellId: parseUserSetting(settings[CELL_BOARD_M_CELL_ID], EMPTY_GUID),
});

/**
 * Hook that returns the MCell for a given mCellId. Returns undefined if not found.
 * @param mCellId
 */
export const useMCellSelector = (mCellId: string): MCell | undefined => {
  const { data: mCells } = useAllMCellsQuery();
  return mCells?.find((m) => m.id === mCellId);
};

/**
 * Selects the global settings (feature flags and banner) from user settings
 */
const selectGlobalSettings = (settings: UserSetting) => {
  const isEnabled = (key: keyof UserSetting) =>
    parseUserSetting<boolean>(settings[key], false);
  return {
    cellBoardEnabled: isEnabled(CELL_BOARD_ENABLED),
    reportEnabled: isEnabled(REPORT_ENABLED),
    resourceTrackingEnabled: isEnabled(RESOURCE_TRACKING_ENABLED),
    crewingGuideEnabled: isEnabled(CREWING_GUIDE_ENABLED),
    supportHierarchyEnabled: isEnabled(SUPPORT_HIERARCHY_ENABLED),
    schedulingToolEnabled: isEnabled(SCHEDULING_TOOL_ENABLED),
    lswEnabled: isEnabled(LSW_ENABLED),
    coatingsEnabled: isEnabled(COATINGS_ENABLED),
    downtimeEscalationEnabled: isEnabled(DOWNTIME_ESCALATION_ENABLED),
    jobStationsEnabled: isEnabled(JOB_STATIONS_ENABLED),
    jobStationAssignmentEnabled: isEnabled(JOB_STATION_ASSIGNMENT_ENABLED),
    scorecardEnabled: isEnabled(SCORECARD_ENABLED),
    mCellStatusEnabled: isEnabled(M_CELL_STATUS_ENABLED),
    bannerInfo: parseUserSetting(settings[BANNER_INFO], {} as any),
    lmsEnrollmentEnabled: isEnabled(LMS_ENROLLMENT_ENABLED),
    cellBoard2Enabled: isEnabled(CELL_BOARD_2_0_ENABLED),
  };
};

/**
 * Hook that returns the current state of the global settings
 */
export const useGlobalSettings = () => useUserSettings(selectGlobalSettings);
